import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { decodeToken } from "../lib/tokenUtils";
import { API_BASE_URL } from "@/lib/apiBase";
import { toastError } from "@/lib/toast";
import { fetchUserMe } from "@/lib/userMeCache";
import { isSsoRequiredError, ssoErrorMessage, isOrgSsoEnterPath, orgSsoEnterUrl } from "@/lib/orgSso";
import WorkspaceUpdatingOverlay from "../components/WorkspaceUpdatingOverlay";

const OrgFilterContext = createContext(null);

const SOCIAL = "social";
const ENTER_OVERLAY_DELAY_MS = 400;
const ENTER_HARD_CAP_MS = 30000;

const storageKeyForUser = (userId) =>
  userId ? `selectedOrgId:${userId}` : "selectedOrgId";

/** @returns {string|null} stored workspace id, or null when unset */
const readStoredSelection = (userId) => {
  try {
    const saved = localStorage.getItem(storageKeyForUser(userId));
    if (saved === null || saved === "") {
      return null;
    }
    return saved === SOCIAL ? SOCIAL : saved;
  } catch (error) {
    console.error("Failed to load workspace selection from localStorage:", error);
    return null;
  }
};

const writeStoredSelection = (userId, value) => {
  try {
    localStorage.setItem(storageKeyForUser(userId), value);
  } catch (error) {
    console.error("Failed to save workspace selection to localStorage:", error);
  }
};

const clearStoredSelection = (userId) => {
  try {
    localStorage.removeItem(storageKeyForUser(userId));
    // Migrate away from the legacy unscoped key as well.
    localStorage.removeItem("selectedOrgId");
  } catch (error) {
    console.error("Failed to clear workspace selection from localStorage:", error);
  }
};

const normalizeDefaultWorkspace = (value) => {
  if (value?.type === "organization" && value?.org_id) {
    return { type: "organization", org_id: value.org_id };
  }
  return { type: "social", org_id: null };
};

const defaultWorkspaceToSelection = (defaultWorkspace) =>
  defaultWorkspace?.type === "organization" && defaultWorkspace.org_id
    ? defaultWorkspace.org_id
    : SOCIAL;

/** Workspace implied by the URL so refresh stays on /c and org Circa Social. */
export function inferWorkspaceFromPath(pathname) {
  const p = String(pathname || "");
  const orgSocial = p.match(/^\/orgs\/([^/]+)\/social(?:\/|$)/);
  if (orgSocial) {
    try {
      return decodeURIComponent(orgSocial[1]);
    } catch {
      return orgSocial[1];
    }
  }
  const orgWorkspace =
    p.match(/^\/orgs\/([^/]+)(?:\/|$)/) || p.match(/^\/o\/([^/]+)(?:\/|$)/);
  if (orgWorkspace) {
    try {
      return decodeURIComponent(orgWorkspace[1]);
    } catch {
      return orgWorkspace[1];
    }
  }
  if (
    p === "/explore" ||
    p.startsWith("/explore/") ||
    p.startsWith("/c/") ||
    p.startsWith("/u/") ||
    p.startsWith("/org/") ||
    p === "/messages" ||
    p.startsWith("/messages/") ||
    p.startsWith("/communities") ||
    p.startsWith("/social/") ||
    p.startsWith("/p/")
  ) {
    return SOCIAL;
  }
  return null;
}

function isAbortError(err) {
  return (
    axios.isCancel?.(err) ||
    err?.code === "ERR_CANCELED" ||
    err?.name === "CanceledError"
  );
}

export const OrgFilterProvider = ({ children }) => {
  const { accessToken } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const userId = useMemo(() => {
    const payload = decodeToken(accessToken);
    return payload?.sub || payload?.user_id || null;
  }, [accessToken]);

  const [selectedOrgId, setSelectedOrgIdState] = useState(SOCIAL);
  const [defaultWorkspace, setDefaultWorkspace] = useState({
    type: "social",
    org_id: null,
  });
  const [workspaceReady, setWorkspaceReady] = useState(false);
  const [workspaceUpdating, setWorkspaceUpdating] = useState(false);
  const [workspaceRefreshKey, setWorkspaceRefreshKey] = useState(0);
  const [workspaceSnapshot, setWorkspaceSnapshot] = useState(null);
  const lastUserIdRef = useRef(null);
  /** Skip re-bootstrap on token refresh for the same user (preserves mid-session workspace). */
  const bootstrappedUserIdRef = useRef(null);
  const selectedOrgIdRef = useRef(selectedOrgId);
  const enterGenRef = useRef(0);
  const enterStartedAtRef = useRef(0);
  const workspaceUpdatingRef = useRef(false);
  const workspaceSnapshotRef = useRef(null);
  const onSsoEnterPathRef = useRef(false);
  selectedOrgIdRef.current = selectedOrgId;
  workspaceUpdatingRef.current = workspaceUpdating;
  workspaceSnapshotRef.current = workspaceSnapshot;

  useEffect(() => {
    if (userId) {
      lastUserIdRef.current = userId;
    }

    if (!accessToken || !userId) {
      if (lastUserIdRef.current) {
        clearStoredSelection(lastUserIdRef.current);
        lastUserIdRef.current = null;
      } else {
        clearStoredSelection(null);
      }
      bootstrappedUserIdRef.current = null;
      setSelectedOrgIdState(SOCIAL);
      setDefaultWorkspace({ type: "social", org_id: null });
      setWorkspaceReady(false);
      setWorkspaceUpdating(false);
      setWorkspaceSnapshot(null);
      return;
    }

    // Token refresh for the same user must not reset the selected workspace.
    if (bootstrappedUserIdRef.current === userId) {
      return;
    }

    let cancelled = false;

    const bootstrap = async () => {
      setWorkspaceReady(false);
      try {
        const [meData, membershipsRes] = await Promise.all([
          fetchUserMe(accessToken),
          axios.get(`${API_BASE_URL}/org/memberships`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
        ]);

        if (cancelled) return;

        const membershipIds = new Set(
          (membershipsRes.data || []).map((org) => org.org_id)
        );
        let resolvedDefault = normalizeDefaultWorkspace(
          meData?.default_workspace
        );
        if (
          resolvedDefault.type === "organization" &&
          !membershipIds.has(resolvedDefault.org_id)
        ) {
          resolvedDefault = { type: "social", org_id: null };
        }

        // Refresh restores the last mid-session workspace from localStorage when
        // still valid. Fresh login has no stored selection (cleared on logout)
        // and opens the starred default. Token refresh must not re-run this path
        // (see bootstrappedUserIdRef guard above).
        const stored = readStoredSelection(userId);
        const storedIsValid =
          stored === SOCIAL ||
          (stored != null && membershipIds.has(stored));
        const fromUrl = inferWorkspaceFromPath(
          typeof window !== "undefined" ? window.location.pathname : ""
        );
        const urlIsValid =
          fromUrl === SOCIAL ||
          (fromUrl != null && membershipIds.has(fromUrl));
        const nextSelection = urlIsValid
          ? fromUrl
          : storedIsValid
            ? stored
            : defaultWorkspaceToSelection(resolvedDefault);

        setDefaultWorkspace(resolvedDefault);
        setSelectedOrgIdState(nextSelection);
        writeStoredSelection(userId, nextSelection);
        setWorkspaceUpdating(false);
      } catch (error) {
        console.error("Failed to bootstrap workspace preference:", error);
        if (!cancelled) {
          const fromUrl = inferWorkspaceFromPath(
            typeof window !== "undefined" ? window.location.pathname : ""
          );
          const fallback =
            fromUrl || readStoredSelection(userId) || SOCIAL;
          setDefaultWorkspace({ type: "social", org_id: null });
          setSelectedOrgIdState(fallback);
          setWorkspaceUpdating(false);
        }
      } finally {
        if (!cancelled) {
          bootstrappedUserIdRef.current = userId;
          setWorkspaceReady(true);
        }
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [accessToken, userId]);

  useEffect(() => {
    onSsoEnterPathRef.current = isOrgSsoEnterPath(location.pathname);
    if (onSsoEnterPathRef.current) {
      setWorkspaceUpdating(false);
    }
  }, [location.pathname]);

  // Single workspace/enter round-trip: schema + SSO + shell chrome.
  useEffect(() => {
    if (!accessToken || !workspaceReady) return;
    if (!selectedOrgId || selectedOrgId === SOCIAL) {
      setWorkspaceUpdating(false);
      setWorkspaceSnapshot(null);
      return;
    }
    if (onSsoEnterPathRef.current) {
      return;
    }

    if (workspaceSnapshotRef.current?.orgId === selectedOrgId) {
      setWorkspaceUpdating(false);
      return;
    }

    const gen = ++enterGenRef.current;
    enterStartedAtRef.current = Date.now();
    const controller = new AbortController();
    let overlayTimer = null;
    let settled = false;

    const clearEnterUi = () => {
      if (overlayTimer != null) {
        clearTimeout(overlayTimer);
        overlayTimer = null;
      }
      setWorkspaceUpdating(false);
    };

    // Delay overlay so fast SSO-required responses never flash "Updating".
    overlayTimer = setTimeout(() => {
      if (!settled && gen === enterGenRef.current) {
        setWorkspaceUpdating(true);
      }
    }, ENTER_OVERLAY_DELAY_MS);

    (async () => {
      try {
        const res = await axios.post(
          `${API_BASE_URL}/org/${encodeURIComponent(selectedOrgId)}/workspace/enter`,
          null,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            signal: controller.signal,
          }
        );
        if (gen !== enterGenRef.current) return;
        settled = true;

        const payload = res.data || {};
        if (payload.sso?.required) {
          setWorkspaceSnapshot(null);
          clearEnterUi();
          navigate(
            orgSsoEnterUrl(
              selectedOrgId,
              `${window.location.pathname}${window.location.search}`
            ),
            { replace: true }
          );
          return;
        }

        setWorkspaceSnapshot({
          orgId: selectedOrgId,
          ...payload,
        });
        setWorkspaceRefreshKey((k) => k + 1);
        clearEnterUi();
      } catch (err) {
        if (gen !== enterGenRef.current) return;
        settled = true;
        clearEnterUi();
        if (isAbortError(err)) return;
        setWorkspaceSnapshot(null);
        if (isSsoRequiredError(err)) {
          navigate(
            orgSsoEnterUrl(
              selectedOrgId,
              `${window.location.pathname}${window.location.search}`
            ),
            { replace: true }
          );
          return;
        }
        console.error("Failed to prepare org workspace:", err);
        toastError(err, "Could not prepare this workspace. Please try again.");
      }
    })();

    return () => {
      settled = true;
      if (overlayTimer != null) {
        clearTimeout(overlayTimer);
        overlayTimer = null;
      }
      controller.abort();
      setWorkspaceUpdating(false);
    };
  }, [accessToken, workspaceReady, selectedOrgId, navigate]);

  useEffect(() => {
    const checkHardCap = () => {
      if (document.visibilityState === "hidden") return;
      const orgId = selectedOrgIdRef.current;
      if (!orgId || orgId === SOCIAL) return;
      if (!workspaceUpdatingRef.current) return;

      const elapsed = Date.now() - enterStartedAtRef.current;
      if (elapsed >= ENTER_HARD_CAP_MS) {
        enterGenRef.current += 1;
        setWorkspaceUpdating(false);
        toastError(
          "Couldn't prepare this workspace. Try switching workspace or refreshing."
        );
      }
    };

    document.addEventListener("visibilitychange", checkHardCap);
    window.addEventListener("pageshow", checkHardCap);
    return () => {
      document.removeEventListener("visibilitychange", checkHardCap);
      window.removeEventListener("pageshow", checkHardCap);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get("sso_error");
    if (!code) return;
    if (
      isOrgSsoEnterPath(location.pathname) ||
      (/\/orgs\/[^/]+\/settings/.test(location.pathname) &&
        params.get("section") === "security")
    ) {
      return;
    }
    toastError(ssoErrorMessage(code));
    params.delete("sso_error");
    const q = params.toString();
    window.history.replaceState(
      null,
      "",
      `${location.pathname}${q ? `?${q}` : ""}${location.hash || ""}`
    );
  }, [location.pathname, location.search, location.hash]);

  const setSelectedOrgId = useCallback(
    (value) => {
      const normalizedValue = value === null || value === "" ? SOCIAL : value;
      // Same workspace: no-op.
      if (selectedOrgIdRef.current === normalizedValue) {
        return;
      }
      selectedOrgIdRef.current = normalizedValue;
      setSelectedOrgIdState(normalizedValue);
      writeStoredSelection(userId, normalizedValue);
      if (normalizedValue && normalizedValue !== SOCIAL) {
        if (workspaceSnapshotRef.current?.orgId !== normalizedValue) {
          setWorkspaceSnapshot(null);
        }
        // Enter effect owns Updating timing (delayed after SSO check).
      } else {
        setWorkspaceUpdating(false);
        setWorkspaceSnapshot(null);
      }
    },
    [userId]
  );

  useEffect(() => {
    if (!workspaceReady || !userId) return;
    const fromUrl = inferWorkspaceFromPath(location.pathname);
    if (!fromUrl) return;
    if (selectedOrgIdRef.current === fromUrl) return;
    setSelectedOrgId(fromUrl);
  }, [location.pathname, workspaceReady, userId, setSelectedOrgId]);

  const setDefaultWorkspacePreference = useCallback(
    async (next) => {
      const previous = defaultWorkspace;
      const optimistic = normalizeDefaultWorkspace(next);
      setDefaultWorkspace(optimistic);

      try {
        const body =
          optimistic.type === "organization"
            ? { type: "organization", org_id: optimistic.org_id }
            : { type: "social" };
        const response = await axios.patch(
          `${API_BASE_URL}/user/me/default-workspace`,
          body,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
        const confirmed = normalizeDefaultWorkspace(
          response.data?.default_workspace
        );
        setDefaultWorkspace(confirmed);
        return confirmed;
      } catch (error) {
        setDefaultWorkspace(previous);
        throw error;
      }
    },
    [accessToken, defaultWorkspace]
  );

  const value = useMemo(
    () => ({
      selectedOrgId,
      setSelectedOrgId,
      defaultWorkspace,
      setDefaultWorkspacePreference,
      workspaceReady,
      workspaceUpdating,
      workspaceRefreshKey,
      workspaceSnapshot,
    }),
    [
      selectedOrgId,
      setSelectedOrgId,
      defaultWorkspace,
      setDefaultWorkspacePreference,
      workspaceReady,
      workspaceUpdating,
      workspaceRefreshKey,
      workspaceSnapshot,
    ]
  );

  return (
    <OrgFilterContext.Provider value={value}>
      {children}
      <WorkspaceUpdatingOverlay open={workspaceUpdating} />
    </OrgFilterContext.Provider>
  );
};

export const useOrgFilter = () => {
  const context = useContext(OrgFilterContext);
  if (!context) {
    throw new Error("useOrgFilter must be used within OrgFilterProvider");
  }
  return context;
};
